/* eslint-disable no-underscore-dangle */
/* eslint-disable no-console */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import * as Yup from 'yup';
import Swal from 'sweetalert2';
import { Form } from '@unform/web';
import { FormHandles } from '@unform/core';

import { HiPlus } from 'react-icons/hi';
import { FaFolder, FaBolt } from 'react-icons/fa';

import { Container, TreeCard, EditorCard, PromptEditor, Modal } from './styles';

import Input from '~/components/Input';
import Select, { IOption } from '~/components/Select';
import Textarea from '~/components/Textarea';
import Loading from '~/components/Loading';
import { KrClose } from '~/components/KoroIcons';
import Toast from '~/utils/toast';
import getValidationErros from '~/utils/getValidationsErrors';
import api from '~/services/api';

/**
 * Contrato de API esperado pelo backend (ajustar os endpoints/campos abaixo
 * conforme a implementação real do serviço do chatbot):
 *
 * GET  /ai-agents
 *   -> { fixed: IAgentNode[], departments: IDepartment[] }
 *
 * POST /ai-agents
 *   <- { name, key, department_id: number | null, ai_model }
 *   -> IAgentNode criado
 *
 * PUT  /ai-agents/:id
 *   <- { ai_model, status, required_variables, system_prompt }
 *   -> IAgentNode atualizado (backend deve incrementar "version")
 */

const AI_MODELS = [
  'gpt-4o-mini',
  'GPT-4o',
  'GPT-5',
  'Claude 3.5 Sonnet',
  'Claude Sonnet 4.5',
  'Gemini 2.0 Flash',
];

interface IPromptResponse {
  _id: string;
  key: string;
  version: number;
  language: string;
  content: string;
  description: string;
  metadata: {
    ui_group: string;
    ui_label: string;
    model: string;
    temperature: number;
    max_tokens: number;
    top_p: number;
    frequency_penalty: number;
    presence_penalty: number;
  };
}

interface IAgent {
  id: string;
  key: string;
  name: string;
  description: string;
  content: string;
  version: number;
  model: string;
  department: string;
}

interface IDepartment {
  id: string;
  name: string;
  agents: IAgent[];
}

const AiAgents: React.FC = () => {
  const formRef = useRef<FormHandles>(null);
  const newAgentFormRef = useRef<FormHandles>(null);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [show, setShow] = useState(false);

  const [fixedAgents, setFixedAgents] = useState<IAgent[]>([]);
  const [departments, setDepartments] = useState<IDepartment[]>([]);

  const [agentSelected, setAgentSelected] = useState<IAgent | null>(null);
  const [departmentSelected, setDepartmentSelected] = useState('');
  const [departamentName, setDepartamentName] = useState('');
  const [departament, setDepartament] = useState('');
  const [name, setName] = useState('');

  const handleLoadAgents = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get<IPromptResponse[]>('ai/prompts');

      const departamentNames = Array.from(
        new Set(response.data.map((item) => item.metadata?.ui_group))
      );

      const departamentsData = departamentNames
        .filter((nameData) => nameData !== 'Fixos')
        .map((nameData) => ({
          id: `department-${nameData}`,
          name: nameData,
          agents: response.data
            .filter((item) => item.metadata.ui_group === nameData)
            .map((item) => ({
              id: item._id,
              key: item.key,
              name: item.metadata.ui_label,
              description: item.description,
              content: item.content,
              version: item.version,
              model: item.metadata.model,
              department: item.metadata.ui_group,
            })),
        }));

      setFixedAgents(
        response.data
          .filter((item) => item.metadata.ui_group === 'Fixos')
          .map((item) => ({
            id: item._id,
            key: item.key,
            name: item.metadata.ui_label,
            description: item.description,
            content: item.content,
            version: item.version,
            model: item.metadata.model,
            department: item.metadata.ui_group,
          }))
      );
      setDepartments(departamentsData);
    } catch (error) {
      setFixedAgents([]);
      setDepartments([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    handleLoadAgents();
  }, [handleLoadAgents]);

  const handleSelectAgent = useCallback(
    (agent: IAgent, departmentName?: string) => {
      setAgentSelected(agent);
      setDepartmentSelected(departmentName || 'Fixos');
    },
    []
  );

  const aiModelOptions = useMemo<IOption[]>(() => {
    console.log('agentSelected', agentSelected);
    return AI_MODELS.map((model) => ({
      id: model,
      value: model,
      selected: agentSelected?.model === model,
    }));
  }, [agentSelected]);

  const newAgentAiModelOptions = useMemo<IOption[]>(
    () =>
      AI_MODELS.map((model, index) => ({
        id: model,
        value: model,
        selected: index === 0,
      })),
    []
  );

  // const handleUpdateAgentInState = useCallback((updatedAgent: IAgent) => {
  //   setFixedAgents((oldState) =>
  //     oldState.map((agent) =>
  //       agent.id === updatedAgent.id ? updatedAgent : agent
  //     )
  //   );
  //   setDepartments((oldState) =>
  //     oldState.map((department) => ({
  //       ...department,
  //       agents: department.agents.map((agent) =>
  //         agent.id === updatedAgent.id ? updatedAgent : agent
  //       ),
  //     }))
  //   );
  //   setAgentSelected(updatedAgent);
  // }, []);

  const handleSubmitPrompt = useCallback(
    async (dataSubmit) => {
      if (!agentSelected) {
        return;
      }

      try {
        formRef.current?.setErrors({});

        const schema = Yup.object().shape({
          required_variables: Yup.string(),
          content: Yup.string().required('O prompt é obrigatório'),
        });

        await schema.validate(dataSubmit, { abortEarly: false });

        setSaving(true);

        if (
          !agentSelected.content ||
          (agentSelected.content !== dataSubmit.content &&
            agentSelected.model !== dataSubmit.ai_model)
        ) {
          await api.put<IAgent>(`ai/prompts/${agentSelected.id}`, {
            content: dataSubmit.content,
            metadata: {
              ui_group: agentSelected.department,
              ui_label: agentSelected.name,
              model: dataSubmit.ai_model,
            },
          });

          setAgentSelected((prevAgent: any) => ({
            ...prevAgent,
            content: dataSubmit.content,
            model: dataSubmit.ai_model,
          }));
        } else {
          const response = await api.post(`ai/prompts`, {
            key: dataSubmit.key,
            version: agentSelected.version + 1,
            language: 'pt-BR',
            content: dataSubmit.content,
            description: agentSelected.description,
            metadata: {
              ui_group: agentSelected.department,
              ui_label: agentSelected.name,
              model: dataSubmit.ai_model,
            },
          });

          setAgentSelected((prevAgent: any) => ({
            ...prevAgent,
            id: response.data._id,
            content: dataSubmit.content,
            model: dataSubmit.ai_model,
            version: response.data.version,
          }));

          await api.delete(`ai/prompts/${agentSelected.id}`);

          await handleLoadAgents();
        }

        // handleUpdateAgentInState(response.data);

        Toast.fire(
          'Sucesso!',
          'Nova versão do prompt salva com sucesso',
          'success'
        );
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErros(error);
          console.log('errors', errors);
          formRef.current?.setErrors(errors);
        } else {
          Swal.fire('Oops...', 'Ocorreu um erro, tente novamente por favor');
        }
      } finally {
        setSaving(false);
      }
    },
    [agentSelected, handleLoadAgents]
  );

  const handleClose = useCallback(() => {
    if (newAgentFormRef.current) {
      newAgentFormRef.current.reset();
    }
    setDepartamentName('');
    setDepartament('');
    setShow(false);
    setName('');
  }, []);

  const handleCreateAgent = useCallback(
    async (dataSubmit) => {
      try {
        newAgentFormRef.current?.setErrors({});

        const schema = Yup.object().shape({
          name: Yup.string().required('O nome é obrigatório'),
          key: Yup.string().required('A chave do sistema é obrigatória'),
          department: Yup.string().required('O departamento é obrigatório'),
          ai_model: Yup.string().test(
            'ai_model',
            'O modelo AI é obrigatório',
            (value) => !!value && value !== 'Selecione'
          ),
          description: Yup.string().required('A descrição é obrigatória'),
        });

        await schema.validate(dataSubmit, { abortEarly: false });

        setCreating(true);

        await api.post('ai/prompts', {
          key: dataSubmit.key,
          version: 1,
          language: 'pt-BR',
          content: '',
          description: dataSubmit.description,
          metadata: {
            ui_group: dataSubmit.department,
            ui_label: dataSubmit.name,
            model: dataSubmit.ai_model,
          },
        });

        await handleLoadAgents();

        Toast.fire('Sucesso!', 'Agente criado com sucesso', 'success');
        handleClose();
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErros(error);
          newAgentFormRef.current?.setErrors(errors);
        } else {
          Swal.fire('Oops...', 'Ocorreu um erro, tente novamente por favor');
        }
      } finally {
        setCreating(false);
      }
    },
    [handleClose, handleLoadAgents]
  );

  const handleChangeName = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setName(event.target.value);
    },
    []
  );

  const handleClickNewAgent = useCallback((departmentData?: string) => {
    if (departmentData) {
      setDepartamentName(departmentData);
      setDepartament(departmentData);
    }

    setShow(true);
  }, []);

  const handleChangeDepartament = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const departamentData = event.target.value;
      setDepartament(departamentData);
    },
    []
  );

  const key = useMemo(() => {
    if (departamentName && departamentName !== 'Fixos') {
      if (name) {
        return `agent.${departamentName
          .trim()
          .toLowerCase()
          .replace(/\s+(.)/g, (_, chr) => chr.toUpperCase())}.${name
          .trim()
          .toLowerCase()
          .replace(/\s+(.)/g, (_, chr) => chr.toUpperCase())}`;
      }

      return `agent.${departamentName
        .trim()
        .toLowerCase()
        .replace(/\s+(.)/g, (_, chr) => chr.toUpperCase())}`;
    }

    if (departament && departament !== 'Fixos') {
      if (name) {
        return `agent.${departament
          .trim()
          .toLowerCase()
          .replace(/\s+(.)/g, (_, chr) => chr.toUpperCase())}.${name
          .trim()
          .toLowerCase()
          .replace(/\s+(.)/g, (_, chr) => chr.toUpperCase())}`;
      }

      return `agent.${departament
        .trim()
        .toLowerCase()
        .replace(/\s+(.)/g, (_, chr) => chr.toUpperCase())}`;
    }

    if (name) {
      return `agent.${name
        .trim()
        .toLowerCase()
        .replace(/\s+(.)/g, (_, chr) => chr.toUpperCase())}`;
    }

    return 'agent';
  }, [departament, departamentName, name]);

  const handleModelChange = useCallback(
    (model) => {
      if (agentSelected) {
        setAgentSelected({ ...agentSelected, model: model.id });
      }
    },
    [agentSelected]
  );

  return (
    <Container>
      <div className="container-fluid">
        <div className="row">
          <div className="col-12 px-0">
            <div className="bg-white d-flex align-items-center p-4">
              <h1 className="mb-0">Configuração de Agentes IA</h1>
            </div>
          </div>
        </div>

        <div className="row mt-3">
          <div className="col-lg-4 px-0 pe-lg-3">
            <TreeCard className="bg-white">
              <div className="tree-header d-flex justify-content-between align-items-center p-3">
                <h2 className="h6 fw-semibold mb-0">Hierarquia</h2>
              </div>
              <div className="tree-body p-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <div className="tree-group-title">Fixos</div>
                  <button
                    type="button"
                    className="border-0 p-2 d-flex align-items-center justify-content-center btn-new-agent"
                    onClick={() => handleClickNewAgent('Fixos')}
                  >
                    <HiPlus size={14} color="#ff6900" />
                  </button>
                </div>
                <ul className="list-unstyled mb-4">
                  {fixedAgents.map((agent) => (
                    <li key={agent.id}>
                      <button
                        type="button"
                        className={`tree-item ${
                          agentSelected?.id === agent.id ? 'active' : ''
                        }`}
                        onClick={() => handleSelectAgent(agent)}
                      >
                        <FaBolt size={13} />
                        {agent.name}
                      </button>
                    </li>
                  ))}
                  {fixedAgents.length === 0 && !loading && (
                    <li className="hint-text px-2">
                      Nenhum agente fixo cadastrado
                    </li>
                  )}
                </ul>

                <div className="d-flex justify-content-between align-items-center mb-2">
                  <div className="tree-group-title mb-2">Departamentos</div>
                  <button
                    type="button"
                    className="border-0 p-2 d-flex align-items-center justify-content-center btn-new-agent"
                    onClick={() => handleClickNewAgent()}
                  >
                    <HiPlus size={14} color="#ff6900" />
                  </button>
                </div>
                {departments.map((department) => (
                  <div key={department.id} className="mb-1">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <div className="d-flex align-items-center px-3">
                        <FaFolder size={14} className="folder-icon me-2" />
                        {department.name}
                      </div>
                      <button
                        type="button"
                        className="border-0 p-2 d-flex align-items-center justify-content-center btn-new-agent"
                        onClick={() => handleClickNewAgent(department.name)}
                      >
                        <HiPlus size={14} color="#ff6900" />
                      </button>
                    </div>
                    <ul className="tree-sublist list-unstyled">
                      {department.agents.map((agent) => (
                        <li key={agent.id}>
                          <button
                            type="button"
                            className={`tree-item ${
                              agentSelected?.id === agent.id ? 'active' : ''
                            }`}
                            onClick={() =>
                              handleSelectAgent(agent, department.name)
                            }
                          >
                            {agent.name}
                          </button>
                        </li>
                      ))}
                      {department.agents.length === 0 && (
                        <li className="hint-text px-2">
                          Nenhum subagente cadastrado
                        </li>
                      )}
                    </ul>
                  </div>
                ))}
                {departments.length === 0 && !loading && (
                  <p className="hint-text px-2">
                    Nenhum departamento cadastrado
                  </p>
                )}
              </div>
            </TreeCard>
          </div>

          <div className="col-lg-8 px-0 mt-3 mt-lg-0">
            <EditorCard className="bg-white">
              {agentSelected ? (
                <Form
                  ref={formRef}
                  key={agentSelected.id}
                  initialData={agentSelected}
                  onSubmit={handleSubmitPrompt}
                  className="d-flex flex-column h-100"
                >
                  <div className="p-4 pb-3 d-flex justify-content-between align-items-start flex-wrap gap-3">
                    <div>
                      <div className="breadcrumb mb-1">
                        {departmentSelected !== 'Fixos' ? (
                          <>
                            Departamentos <span className="mx-1">/</span>
                          </>
                        ) : (
                          ''
                        )}
                        {departmentSelected} <span className="mx-1">/</span>{' '}
                        <span className="fw-medium text-dark">
                          {agentSelected.name}
                        </span>
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        <h2 className="h4 fw-bold mb-0">
                          {agentSelected.name}
                        </h2>
                        <span className="badge-status badge-version">
                          v. {agentSelected.version}
                        </span>
                      </div>
                    </div>
                    <button
                      type="submit"
                      className="btn-new text-white px-4 py-2 border-0"
                      disabled={saving}
                    >
                      {saving ? 'Salvando...' : 'Salvar Nova Versão'}
                    </button>
                  </div>
                  <div className="px-4 pb-3 d-flex align-items-center gap-2">
                    <small className="text-muted">
                      {agentSelected.description}
                    </small>
                  </div>

                  <div className="editor-body px-4 pb-4">
                    <div className="row">
                      <div className="col-md-6">
                        <h4 className="label">Chave do Sistema</h4>
                        <Input name="key" className="height" disabled />
                      </div>
                      <div className="col-md-6 mt-3 mt-md-0">
                        <h4 className="label">Modelo de IA Preferido</h4>
                        <Select
                          name="model"
                          className="height"
                          options={[
                            {
                              id: 'Selecione',
                              value: 'Selecione',
                              selected: false,
                              notSelectable: true,
                            },
                            ...aiModelOptions,
                          ]}
                          onChange={handleModelChange}
                        />
                      </div>
                      {/* <div className="col-12 mt-3">
                        <h4 className="label">Variáveis Obrigatórias</h4>
                        <Input name="required_variables" className="height" />
                      </div> */}
                      {/* <div className="col-12 mt-2">
                        <p className="hint-text mb-0">
                          O orquestrador garantirá que estes dados sejam
                          injetados antes da chamada.
                        </p>
                      </div> */}

                      <div className="col-12 mt-4">
                        <h4 className="label">System Prompt</h4>
                        <PromptEditor>
                          <div className="editor-file-name">
                            system_prompt.txt
                          </div>
                          <Textarea
                            name="content"
                            className="prompt-textarea"
                            height="260px"
                          />
                        </PromptEditor>
                      </div>
                    </div>
                  </div>
                </Form>
              ) : (
                <div className="empty-state">
                  {loading
                    ? 'Carregando agentes...'
                    : 'Selecione um agente na hierarquia ao lado para configurar o prompt'}
                </div>
              )}
            </EditorCard>
          </div>
        </div>
      </div>

      <Loading active={loading} />

      <Modal size="lg" centered show={show} onHide={handleClose}>
        <Form ref={newAgentFormRef} onSubmit={handleCreateAgent}>
          <Modal.Header className="justify-content-between align-items-center border-0 px-3 px-sm-5 pb-0 pt-0">
            <Modal.Title className="mb-0 mt-4">Novo Agente</Modal.Title>
            <button
              type="button"
              onClick={handleClose}
              className="border-0 bg-transparent"
            >
              <KrClose size={20} color="#989898" />
            </button>
          </Modal.Header>
          <Modal.Body className="container pt-0 px-3 px-sm-5">
            <div className="row">
              <div className="col-12">
                <hr className="mb-4" />
              </div>
              <div className="col-lg-6">
                <h4 className="h6 fw-normal">Nome do Agente</h4>
                <Input
                  name="name"
                  type="text"
                  className="height"
                  onChange={handleChangeName}
                  value={name}
                />
              </div>
              <div className="col-lg-6 mt-3 mt-lg-0">
                <h4 className="h6 fw-normal">Chave do Sistema</h4>
                <Input
                  name="key"
                  type="text"
                  className="height disabled"
                  value={key}
                  disabled
                />
              </div>
              <div className="col-lg-6 mt-3">
                <h4 className="h6 fw-normal">Departamento</h4>
                <Input
                  name="department"
                  className={`height ${departamentName ? 'disabled' : ''}`}
                  value={departament}
                  onChange={handleChangeDepartament}
                  disabled={!!departamentName}
                />
              </div>
              <div className="col-lg-6 mt-3">
                <h4 className="h6 fw-normal">Modelo de IA Preferido</h4>
                <Select
                  name="ai_model"
                  className="height"
                  placeholder="Selecione"
                  options={[
                    {
                      id: 'Selecione',
                      value: 'Selecione',
                      selected: true,
                      notSelectable: true,
                    },
                    ...newAgentAiModelOptions,
                  ]}
                />
              </div>
              <div className="col-12 mt-3">
                <h4 className="h6 fw-normal">Descrição</h4>
                <Textarea name="description" rows={4} />
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer className="border-0 px-3 px-sm-5 pb-4">
            <button
              type="button"
              className="btn btn-outline-secondary px-4 py-2"
              onClick={handleClose}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn btn-primary text-white px-4 py-2 border-0"
              disabled={creating}
            >
              {creating ? 'Criando...' : 'Criar Agente'}
            </button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
};

export default AiAgents;
